import { useRef, useState, type DragEvent, type HTMLAttributes, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { GripVertical, CornerDownRight, LoaderCircle } from "lucide-react";
import { dropPosition, entityMove, compareTreeOrder, type DropPosition, type TreeItem, type TreeMove } from "@/lib/entityTree";
import { errorMessage } from "@/lib/api";
import { toast } from "@/lib/toast";

type Destination = { id: string | null; position: DropPosition };
export type TreeDragController = ReturnType<typeof useTreeDrag>;

export function useTreeDrag({ items, disabled, onMove, onExpand, canDrop }: {
  items: TreeItem[];
  disabled: boolean;
  onMove: (id: string, move: TreeMove) => Promise<unknown>;
  onExpand: (id: string) => void;
  canDrop?: (source: string, target: string | null, position: DropPosition) => boolean;
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState<string | null>(null);
  const source = useRef<string | null>(null);
  const hover = useRef<{ id: string; since: number } | null>(null);
  const [over, setOver] = useState<Destination | null>(null);
  const [pending, setPending] = useState(false);
  const saving = useRef(false);
  const [announcement, setAnnouncement] = useState("");
  const reset = () => { source.current = null; setActive(null); setOver(null); hover.current = null; };
  const allowed = (id: string, destination: Destination) => !disabled && !saving.current
    && (!canDrop || canDrop(id, destination.id, destination.position))
    && entityMove(items, id, destination.id, destination.position);
  const commit = async (id: string, destination: Destination) => {
    const patch = allowed(id, destination);
    reset();
    if (!patch) return;
    saving.current = true;
    setPending(true);
    try {
      await onMove(id, patch);
      if (patch.parent_id) onExpand(patch.parent_id);
      setAnnouncement(t("tree.moved"));
    } catch (error) {
      toast(errorMessage(error), { tone: "error" });
      setAnnouncement(t("tree.failed"));
    } finally { saving.current = false; setPending(false); }
  };
  const scroll = (event: DragEvent<HTMLElement>) => {
    const container = event.currentTarget.closest<HTMLElement>("[data-tree-scroll]");
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const speed = event.clientY < rect.top + 48 ? -24 : event.clientY > rect.bottom - 48 ? 24 : 0;
    if (speed) container.scrollBy({ top: speed });
  };
  const destination = (event: DragEvent<HTMLElement>, id: string | null): Destination => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { id, position: id ? dropPosition(event.clientY, rect.top, rect.height) : "after" };
  };
  const preview = (event: DragEvent<HTMLDivElement>, id: string | null) => {
    if (!source.current) return;
    event.preventDefault();
    event.stopPropagation();
    scroll(event);
    const next = destination(event, id);
    if (!allowed(source.current, next)) { event.dataTransfer.dropEffect = "none"; setOver(null); return; }
    event.dataTransfer.dropEffect = "move";
    setOver((previous) => previous?.id === next.id && previous.position === next.position ? previous : next);
    if (id && next.position === "inside") {
      if (hover.current?.id !== id) hover.current = { id, since: Date.now() };
      else if (Date.now() - hover.current.since > 650) onExpand(id);
    } else hover.current = null;
  };
  const rowProps = (id: string | null): HTMLAttributes<HTMLDivElement> => ({
    onDragEnter: (event) => preview(event, id),
    onDragOver: (event) => preview(event, id),
    onDragLeave: (event) => {
      if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setOver((value) => value?.id === id ? null : value);
    },
    onDrop: (event) => {
      const idToMove = source.current;
      if (!idToMove) return;
      event.preventDefault();
      event.stopPropagation();
      void commit(idToMove, destination(event, id));
    },
  });
  const handle = (item: TreeItem, label: string) => disabled ? null : <button type="button" draggable={!pending}
    disabled={pending} aria-label={t("tree.drag", { name: label })} title={t("tree.dragHelp")}
    className="flex h-6 w-4 shrink-0 cursor-grab items-center justify-center rounded text-faint opacity-0 hover:bg-active hover:text-fg focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 active:cursor-grabbing disabled:cursor-wait"
    onClick={(event) => event.stopPropagation()}
    onDragStart={(event) => {
      if (disabled || saving.current) { event.preventDefault(); return; }
      source.current = item.id; setActive(item.id); setAnnouncement(t("tree.dragHelp"));
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("application/x-tern-tree", item.id);
      const row = event.currentTarget.closest<HTMLElement>("[data-tree-id]");
      if (row) event.dataTransfer.setDragImage(row, 16, 16);
      event.stopPropagation();
    }} onDragEnd={reset}
    onKeyDown={(event) => {
      if (event.key === "Escape") { reset(); return; }
      if (!event.altKey || !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      const siblings = items.filter((node) => (node.parent_id ?? null) === (item.parent_id ?? null)).sort(compareTreeOrder);
      const index = siblings.findIndex((node) => node.id === item.id);
      const target = event.key === "ArrowUp" || event.key === "ArrowRight" ? siblings[index - 1] : event.key === "ArrowDown" ? siblings[index + 1] : items.find((node) => node.id === item.parent_id);
      if (target) void commit(item.id, { id: target.id, position: event.key === "ArrowRight" ? "inside" : event.key === "ArrowUp" ? "before" : "after" });
    }}><GripVertical className="h-3.5 w-3.5" /></button>;
  return { active, over, pending, announcement, rowProps, handle };
}

export function TreeDropRow({ tree, id, depth = 0, children }: { tree: TreeDragController; id: string; depth?: number; children: ReactNode }) {
  const position = tree.over?.id === id ? tree.over.position : null;
  return <div {...tree.rowProps(id)} data-tree-id={id} data-drop-position={position ?? undefined} className={`group relative ${tree.active === id ? "opacity-40" : ""} ${position === "inside" ? "bg-accent/10 shadow-[inset_0_0_0_1px_var(--accent)]" : ""}`}>
    {children}
    {position && position !== "inside" && <div className={`pointer-events-none absolute right-0 z-20 h-0.5 bg-accent ${position === "before" ? "top-0" : "bottom-0"}`} style={{ left: 12 + depth * 20 }} />}
  </div>;
}

export function TreeDropRoot({ tree }: { tree: TreeDragController }) {
  const { t } = useTranslation();
  return <>
    {tree.active && <div {...tree.rowProps(null)} data-tree-root className={`m-2 flex min-h-14 items-center justify-center gap-2 rounded-md border border-dashed text-[12px] ${tree.over?.id === null ? "border-accent bg-accent/10 text-accent" : "border-border-strong text-muted"}`}><CornerDownRight className="h-3.5 w-3.5" />{t("tree.moveToRoot")}</div>}
    {tree.pending && <div role="status" className="pointer-events-none sticky bottom-3 mx-auto flex w-fit items-center gap-2 rounded-lg border border-border bg-elevated px-3 py-2 text-[12px] text-muted shadow-lg"><LoaderCircle className="h-3.5 w-3.5 animate-spin" />{t("tree.saving")}</div>}
    <span className="sr-only" role="status" aria-live="polite">{tree.announcement}</span>
  </>;
}
